import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { ResolvedMenuNode } from "~/types/storyblok/storyblok";
import { resolveCategoryPath } from "~/utils/categories/categoryTreeIndex";

/**
 * The header menu for templates that render their own header (gotaplatsensfoto,
 * hogsta, tendenz).
 *
 * The new menu is built from Menu Node blocks in the layout story. A space gets
 * it on its own schedule, so until it has items the old Norce category list
 * (`menu_categories` on the layout) is shown instead, in the same shape so one
 * markup renders both. That covers a space without the block or field, an
 * empty menu, and a failed request for it (the caller passes an empty list).
 *
 * Only the old *categories* are converted here. The old CMS menu links keep
 * rendering through their own block while `usesNewMenu` is false, so a space
 * that has not moved over looks exactly as before.
 */

/**
 * A menu node's link for `:to`, for components that render a menu they were
 * handed and need only its links, not the fallback. Category paths are
 * localized as the old header did (no prefix stripping). Story links get the
 * site folder stripped and the market prefix added; external and anchor links
 * stay as authored.
 */
export const useMenuHref = () => {
	const { resolveMenuHref } = useStoryblokLink();
	const localePath = useLocalePath();

	return (node: ResolvedMenuNode): string =>
		node.type === "category" ? localePath(node.href) : resolveMenuHref(node);
};

export const useMenuWithFallback = (
	source: () => {
		menu?: ResolvedMenuNode[] | null;
		menuCategoriesNorce?: Category[] | null;
	}
) => {
	const { locale } = useI18n();
	const config = useRuntimeConfig();
	const categoryStore = useCategoryStore();
	const hrefOf = useMenuHref();

	// An old Norce category as a menu item, built the way the old header links
	// were: the same indexed path lookup as createCategoryLink (NF-390, so a
	// large menu stays cheap to render), the bare CategoryId as id (element ids
	// such as `navigation-panel-<id>` stay as before), and the same image URL.
	const fromCategory = (
		category: Category,
		tree: Category[] | undefined
	): ResolvedMenuNode => ({
		id: String(category.CategoryId),
		type: "category",
		name: category.Name,
		href: `/${resolveCategoryPath(tree, category.CategoryId)}`,
		imageUrl: category.ImageKey
			? `${config.public.norce.cdn}/${category.ImageKey}`
			: undefined,
		children: (category.Children ?? []).map((child) =>
			fromCategory(child, tree)
		)
	});

	// resolveMenu leaves a story, external or anchor node without a name override
	// nameless, which would render as a blank item. Drop a nameless leaf and
	// replace a nameless group with its children (same rule as breadandboxers'
	// headerMenu).
	const withNames = (nodes: ResolvedMenuNode[]): ResolvedMenuNode[] =>
		nodes.flatMap((node) => {
			const children = withNames(node.children);
			if (node.name?.trim()) return [{ ...node, children }];
			return children;
		});

	const usesNewMenu = computed(() => !!source().menu?.length);

	const items = computed<ResolvedMenuNode[]>(() => {
		if (usesNewMenu.value) return withNames(source().menu ?? []);
		// Read through the store so the items update when the tree arrives.
		const tree = categoryStore.categoryTree[locale.value];
		return (source().menuCategoriesNorce ?? []).map((category) =>
			fromCategory(category, tree)
		);
	});

	// Each template keeps its own "active item" rule, so the fallback behaves
	// as before.
	return { items, usesNewMenu, hrefOf };
};
